<template>
  <div>
    <button
      type="button"
      class="btn btn-primary btn-with-addon"
      @click="showModal"
    >
      <span class="btn-addon">
        <i class="btn-addon-icon fe fe-plus-circle" />
      </span>
      Add Enrollment
    </button>

    <a-modal v-model="visible" title="New Tuition Enrollment" :footer="null" width="750px" @cancel="handleCancel" :afterClose="resetAllData">
      <a-tabs default-active-key="1">
        <!-- Single Student Enrollment -->
        <a-tab-pane key="1" tab="Single Enrollment">
          <a-form :form="singleForm" @submit="handleSingleSubmit">
            <div class="row">
              <div class="col-md-6 col-12">
                <a-form-item label="Academic Session">
                  <a-select
                    v-decorator="['session_id', { rules: [{ required: true, message: 'Please select academic session' }] }]"
                    placeholder="Select Session"
                    @change="handleSessionChange"
                  >
                    <a-select-option v-for="s in sessions" :key="s.id" :value="s.id">
                      {{ s.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
              <div class="col-md-6 col-12">
                <a-form-item label="Class (To Filter Students)">
                  <a-select
                    v-decorator="['class_id']"
                    placeholder="Optional Filter by Class"
                    @change="handleClassChange"
                    allowClear
                  >
                    <a-select-option v-for="c in classes" :key="c.id" :value="c.id">
                      {{ c.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
            </div>

            <a-form-item label="Student">
              <a-select
                show-search
                placeholder="Search and Select Student"
                :filterOption="false"
                @search="onSearchStudent"
                @popupScroll="onScrollStudent"
                v-decorator="['student_id', { rules: [{ required: true, message: 'Please select student' }] }]"
              >
                <a-select-option v-for="student in students" :key="student.id" :value="student.id">
                  {{ student.first_name }} {{ student.last_name }}
                  <span v-if="student.enrollment && student.enrollment.length > 0" class="text-muted ml-1">
                    [{{ student.enrollment[0].school_class ? student.enrollment[0].school_class.name : '' }},
                     {{ student.enrollment[0].section ? student.enrollment[0].section.name : '' }},
                     {{ student.enrollment[0].roll_no ? 'Roll: ' + student.enrollment[0].roll_no : '' }}]
                  </span>
                </a-select-option>
                <template slot="notFoundContent">
                  <a-spin v-if="studentLoading" size="small" />
                  <span v-else>No students found</span>
                </template>
              </a-select>
            </a-form-item>

            <div class="row">
              <div class="col-md-6 col-12">
                <a-form-item label="Tuition Program">
                  <a-select
                    v-decorator="['tuition_program_id', { rules: [{ required: true, message: 'Please select program' }] }]"
                    placeholder="Select Program"
                    @change="handleProgramChange"
                  >
                    <a-select-option v-for="p in programs" :key="p.id" :value="p.id">
                      {{ p.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
              <div class="col-md-6 col-12">
                <a-form-item label="Tuition Session (Batch)">
                  <a-select
                    v-decorator="['tuition_session_id']"
                    placeholder="All Sessions / Select Session"
                    allowClear
                    :disabled="!tuitionSessions.length"
                  >
                    <a-select-option v-for="ts in tuitionSessions" :key="ts.id" :value="ts.id">
                      {{ ts.name }} ({{ ts.default_start_time }} - {{ ts.default_end_time }})
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 col-12">
                <a-form-item label="Start Date">
                  <a-date-picker style="width: 100%" v-decorator="['start_date']" />
                </a-form-item>
              </div>
              <div class="col-md-6 col-12">
                <a-form-item label="End Date">
                  <a-date-picker style="width: 100%" v-decorator="['end_date']" />
                </a-form-item>
              </div>
            </div>

            <div class="text-right mt-4">
              <a-button class="mr-2" @click="handleCancel">Cancel</a-button>
              <a-button type="primary" html-type="submit" :loading="loading">Enroll Student</a-button>
            </div>
          </a-form>
        </a-tab-pane>

        <!-- Bulk Enrollment (By Class) -->
        <a-tab-pane key="2" tab="Bulk Enrollment">
          <a-form :form="bulkForm" @submit="handleBulkSubmit">
            <div class="row">
              <div class="col-md-6 col-12">
                <a-form-item label="Academic Session">
                  <a-select
                    v-decorator="['session_id', { rules: [{ required: true, message: 'Please select academic session' }] }]"
                    placeholder="Select Session"
                    @change="handleBulkSessionChange"
                  >
                    <a-select-option v-for="s in sessions" :key="s.id" :value="s.id">
                      {{ s.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
              <div class="col-md-6 col-12">
                <a-form-item label="Class (Filter)">
                  <a-select
                    v-decorator="['class_id']"
                    placeholder="Optional - Filter by Class"
                    @change="handleBulkClassChange"
                    allowClear
                  >
                    <a-select-option v-for="c in classes" :key="c.id" :value="c.id">
                      {{ c.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
            </div>

            <a-form-item label="Students">
              <a-select
                mode="multiple"
                show-search
                placeholder="Search and Select Students"
                :filterOption="false"
                @search="onBulkSearchStudent"
                @popupScroll="onBulkScrollStudent"
                v-model="selectedBulkStudentIds"
                style="width: 100%"
                :loading="bulkStudentLoading"
              >
                <a-select-option v-for="student in bulkStudents" :key="student.id" :value="student.id">
                  {{ student.first_name }} {{ student.last_name }}
                  <span v-if="student.enrollment && student.enrollment.length > 0" class="text-muted ml-1">
                    [{{ student.enrollment[0].school_class ? student.enrollment[0].school_class.name : '' }}{{ student.enrollment[0].section ? ' / ' + student.enrollment[0].section.name : '' }}]
                  </span>
                </a-select-option>
                <template slot="notFoundContent">
                  <a-spin v-if="bulkStudentLoading" size="small" />
                  <span v-else>No students found</span>
                </template>
              </a-select>
            </a-form-item>

            <div class="row">
              <div class="col-md-6 col-12">
                <a-form-item label="Tuition Program">
                  <a-select
                    v-decorator="['tuition_program_id', { rules: [{ required: true, message: 'Please select program' }] }]"
                    placeholder="Select Program"
                    @change="handleBulkProgramChange"
                  >
                    <a-select-option v-for="p in programs" :key="p.id" :value="p.id">
                      {{ p.name }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
              <div class="col-md-6 col-12">
                <a-form-item label="Tuition Session (Batch)">
                  <a-select
                    v-decorator="['tuition_session_id']"
                    placeholder="All Sessions / Select Session"
                    allowClear
                    :disabled="!bulkTuitionSessions.length"
                  >
                    <a-select-option v-for="ts in bulkTuitionSessions" :key="ts.id" :value="ts.id">
                      {{ ts.name }} ({{ ts.default_start_time }} - {{ ts.default_end_time }})
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 col-12">
                <a-form-item label="Start Date">
                  <a-date-picker style="width: 100%" v-decorator="['start_date']" />
                </a-form-item>
              </div>
              <div class="col-md-6 col-12">
                <a-form-item label="End Date">
                  <a-date-picker style="width: 100%" v-decorator="['end_date']" />
                </a-form-item>
              </div>
            </div>

            <div class="text-right mt-4">
              <a-button class="mr-2" @click="handleCancel">Cancel</a-button>
              <a-button
                type="primary"
                html-type="submit"
                :loading="loading"
                :disabled="!selectedBulkStudentIds.length"
              >
                Enroll {{ selectedBulkStudentIds.length }} Students
              </a-button>
            </div>
          </a-form>
        </a-tab-pane>
      </a-tabs>
    </a-modal>
  </div>
</template>

<script>
export default {
  name: 'AddTuitionEnrollmentModal',
  data() {
    return {
      visible: false,
      loading: false,
      studentLoading: false,
      singleForm: this.$form.createForm(this),
      bulkForm: this.$form.createForm(this),

      sessions: [],
      classes: [],
      programs: [],

      students: [],
      studentSearch: '',
      studentPagination: {
        current: 1,
        last: 1,
        perPage: 20,
        total: 0,
      },
      currentSessionId: null,

      tuitionSessions: [],
      bulkTuitionSessions: [],

      // Bulk student fields
      bulkStudents: [],
      bulkStudentLoading: false,
      bulkStudentSearch: '',
      bulkStudentPagination: {
        current: 1,
        last: 1,
        perPage: 20,
        total: 0,
      },
      selectedBulkStudentIds: [],
    }
  },

  methods: {
    showModal() {
      this.visible = true
      this.fetchInitialData()
    },
    handleCancel() {
      this.visible = false
    },
    resetAllData() {
      this.singleForm.resetFields()
      this.bulkForm.resetFields()
      this.students = []
      this.bulkStudents = []
      this.selectedBulkStudentIds = []
      this.tuitionSessions = []
      this.bulkTuitionSessions = []
      this.studentPagination = { current: 1, last: 1, perPage: 20, total: 0 }
      this.bulkStudentPagination = { current: 1, last: 1, perPage: 20, total: 0 }
    },
    fetchInitialData() {
      // Programs
      this.$http.get('/api/tuition/programs')
        .then(({ data }) => { this.programs = data.data || data })

      // Academic Sessions
      this.$http.get('/api/session')
        .then(({ data }) => {
          this.sessions = data.data
          const current = this.sessions.find(s => s.is_current === 1)
          if (current) {
            this.currentSessionId = current.id
            this.$nextTick(() => {
              this.singleForm.setFieldsValue({ session_id: current.id })
              this.bulkForm.setFieldsValue({ session_id: current.id })
            })
            this.getClasses(current.id)
          }
        })
    },
    getClasses(sessionId) {
      this.$http.get('/api/class', { params: { session_id: sessionId } })
        .then(({ data }) => { this.classes = data.data })
    },
    handleSessionChange(sessionId) {
      this.singleForm.setFieldsValue({ class_id: undefined, student_id: undefined })
      this.students = []
      this.getClasses(sessionId)
    },
    handleClassChange(classId) {
      this.singleForm.setFieldsValue({ student_id: undefined })
      this.students = []
      const sessionId = this.singleForm.getFieldValue('session_id') || this.currentSessionId
      if (classId) {
        this.fetchStudents('', 1, classId, sessionId)
      }
    },
    handleProgramChange(programId) {
      this.singleForm.setFieldsValue({ tuition_session_id: undefined })
      this.tuitionSessions = []
      if (programId) {
        this.$http.get(`/api/tuition/programs/${programId}/sessions`)
          .then(({ data }) => {
            this.tuitionSessions = data.data || data
          })
      }
    },
    // Single Student logic
    fetchStudents(search = '', page = 1, classId = null, sessionId = null) {
      this.studentLoading = true
      this.studentSearch = search
      const params = {
        search,
        page,
        per_page: this.studentPagination.perPage,
      }
      if (classId) params.class_id = classId
      if (sessionId) params.session_id = sessionId

      this.$http.get('/api/student', { params })
        .then(response => {
          const { data, meta } = response.data
          if (page === 1) {
            this.students = data
          } else {
            this.students = this.students.concat(data)
          }
          this.studentPagination.current = meta.current_page
          this.studentPagination.last = meta.last_page
          this.studentPagination.total = meta.total
        })
        .finally(() => {
          this.studentLoading = false
        })
    },
    onSearchStudent(value) {
      const classId = this.singleForm.getFieldValue('class_id')
      const sessionId = this.singleForm.getFieldValue('session_id') || this.currentSessionId
      this.fetchStudents(value, 1, classId, sessionId)
    },
    onScrollStudent(e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      if (scrollTop + clientHeight >= scrollHeight - 10 && !this.studentLoading && this.studentPagination.current < this.studentPagination.last) {
        const classId = this.singleForm.getFieldValue('class_id')
        const sessionId = this.singleForm.getFieldValue('session_id') || this.currentSessionId
        this.fetchStudents(this.studentSearch, this.studentPagination.current + 1, classId, sessionId)
      }
    },
    handleSingleSubmit(e) {
      e.preventDefault()
      this.singleForm.validateFields((err, values) => {
        if (!err) {
          this.loading = true
          const payload = {
            student_id: values.student_id,
            tuition_program_id: values.tuition_program_id,
            tuition_session_id: values.tuition_session_id || null,
            start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
            end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
          }

          this.$http.post('/api/tuition/enrollments', payload)
            .then(() => {
              this.loading = false
              this.visible = false
              this.$emit('created')
              this.singleForm.resetFields()
              this.$notification.success({ message: 'Enrolled', description: 'Student enrolled successfully.' })
            })
            .catch(error => {
              this.loading = false
              this.$neoNotify.error(error)
            })
        }
      })
    },

    // Bulk logic
    handleBulkSessionChange(sessionId) {
      this.bulkForm.setFieldsValue({ class_id: undefined })
      this.bulkStudents = []
      this.selectedBulkStudentIds = []
      this.getClasses(sessionId)
    },
    handleBulkClassChange(classId) {
      this.bulkStudents = []
      this.selectedBulkStudentIds = []
      this.bulkStudentPagination = { current: 1, last: 1, perPage: 20, total: 0 }
      const sessionId = this.bulkForm.getFieldValue('session_id') || this.currentSessionId
      this.fetchBulkStudents('', 1, classId || null, sessionId)
    },
    handleBulkProgramChange(programId) {
      this.bulkForm.setFieldsValue({ tuition_session_id: undefined })
      this.bulkTuitionSessions = []
      if (programId) {
        this.$http.get(`/api/tuition/programs/${programId}/sessions`)
          .then(({ data }) => {
            this.bulkTuitionSessions = data.data || data
          })
      }
    },
    fetchBulkStudents(search = '', page = 1, classId = null, sessionId = null) {
      this.bulkStudentLoading = true
      this.bulkStudentSearch = search
      const params = {
        search,
        page,
        per_page: this.bulkStudentPagination.perPage,
      }
      if (classId) params.class_id = classId
      if (sessionId) params.session_id = sessionId

      this.$http.get('/api/student', { params })
        .then(response => {
          const { data, meta } = response.data
          if (page === 1) {
            this.bulkStudents = data
          } else {
            this.bulkStudents = this.bulkStudents.concat(data)
          }
          this.bulkStudentPagination.current = meta.current_page
          this.bulkStudentPagination.last = meta.last_page
          this.bulkStudentPagination.total = meta.total
        })
        .finally(() => {
          this.bulkStudentLoading = false
        })
    },
    onBulkSearchStudent(value) {
      const classId = this.bulkForm.getFieldValue('class_id')
      const sessionId = this.bulkForm.getFieldValue('session_id') || this.currentSessionId
      this.fetchBulkStudents(value, 1, classId, sessionId)
    },
    onBulkScrollStudent(e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      if (scrollTop + clientHeight >= scrollHeight - 10 && !this.bulkStudentLoading && this.bulkStudentPagination.current < this.bulkStudentPagination.last) {
        const classId = this.bulkForm.getFieldValue('class_id')
        const sessionId = this.bulkForm.getFieldValue('session_id') || this.currentSessionId
        this.fetchBulkStudents(this.bulkStudentSearch, this.bulkStudentPagination.current + 1, classId, sessionId)
      }
    },
    handleBulkSubmit(e) {
      e.preventDefault()
      this.bulkForm.validateFields((err, values) => {
        if (!err && this.selectedBulkStudentIds.length) {
          this.loading = true
          const payload = {
            student_ids: this.selectedBulkStudentIds,
            tuition_program_id: values.tuition_program_id,
            tuition_session_id: values.tuition_session_id || null,
            start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
            end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
          }

          this.$http.post('/api/tuition/enrollments/bulk', payload)
            .then(res => {
              this.loading = false
              this.visible = false
              this.$emit('created')
              this.bulkForm.resetFields()
              this.$notification.success({
                message: 'Bulk Enrollment Complete',
                description: res.data.message || 'Students enrolled successfully.',
              })
            })
            .catch(error => {
              this.loading = false
              this.$neoNotify.error(error)
            })
        }
      })
    },
  },
}
</script>
